/**
 * Intake — captured right after sign-in when the customer has no profile yet.
 *
 * Collects name, phone, email, home address, and "How did you hear about us"
 * (Referral reveals a referrer dropdown + free-text fallback). Creates/links a
 * master (keyed on address), then routes to the dashboard. Owner can follow up
 * even if the customer never starts a project.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createMaster, getReferrers, type Referrer, type MasterStreetMatch } from '../lib/api';

const SOURCES = [
  { code: 'GOOGLE', label: 'Google' },
  { code: 'FACEBOOK', label: 'Facebook' },
  { code: 'INSTAGRAM', label: 'Instagram' },
  { code: 'NEXTDOOR', label: 'Nextdoor' },
  { code: 'YARD_SIGN', label: 'Saw a yard sign' },
  { code: 'REFERRAL', label: 'A referral' },
  { code: 'OTHER', label: 'Something else' },
];

export function Intake(): JSX.Element {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    fullName: '', phone: '', email: '',
    addressLine1: '', addressLine2: '', city: '', state: '', postalCode: '',
    referralSource: '', referralDetail: '',
  });
  const [referrers, setReferrers] = useState<Referrer[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Soft duplicate nudge: set when the server finds an existing record at this street.
  const [dupMatches, setDupMatches] = useState<MasterStreetMatch[] | null>(null);

  useEffect(() => {
    getReferrers().then(setReferrers).catch(() => setReferrers([]));
  }, []);

  function set<K extends keyof typeof form>(key: K, value: string): void {
    setForm((f) => ({ ...f, [key]: value }));
  }

  const isReferral = form.referralSource === 'REFERRAL';
  const canSubmit =
    form.fullName.trim() && form.addressLine1.trim() && form.city.trim() && form.state.trim() && form.postalCode.trim();

  async function create(confirmNewAtStreet: boolean): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const result = await createMaster({
        fullName: form.fullName.trim(),
        ...(form.phone.trim() ? { phone: form.phone.trim() } : {}),
        ...(form.email.trim() ? { email: form.email.trim() } : {}),
        addressLine1: form.addressLine1.trim(),
        ...(form.addressLine2.trim() ? { addressLine2: form.addressLine2.trim() } : {}),
        city: form.city.trim(),
        state: form.state.trim(),
        postalCode: form.postalCode.trim(),
        ...(form.referralSource ? { referralSource: form.referralSource } : {}),
        ...(form.referralDetail.trim() ? { referralDetail: form.referralDetail.trim() } : {}),
        ...(confirmNewAtStreet ? { confirmNewAtStreet: true } : {}),
      });
      if ('possibleDuplicate' in result && result.possibleDuplicate) {
        // Pause and ask the customer to confirm same-property vs different unit.
        setDupMatches(result.matches);
        setBusy(false);
        return;
      }
      navigate('/dashboard');
    } catch {
      setError('We couldn\u2019t save your details just now. Try again in a moment.');
      setBusy(false);
    }
  }

  function submit(e: React.FormEvent): void {
    e.preventDefault();
    if (!canSubmit) return;
    void create(false);
  }

  // Soft duplicate nudge — the customer already has a record at this street.
  if (dupMatches) {
    return (
      <div className="flow flow--single">
        <div className="stage-card">
          <h1>You already have this address</h1>
          <p className="stage-card__lede">
            It looks like you&rsquo;ve got a property at this street already. Is this the same place,
            or a different unit?
          </p>
          <div style={{ marginTop: 'var(--pb-3)' }}>
            {dupMatches.map((m) => (
              <div key={m.id} className="pb-surface" style={{ marginBottom: 'var(--pb-2)' }}>
                <div className="pb-option__label">{m.fullName}</div>
                <div className="estimate__hint">
                  {m.addressLine1}
                  {m.addressLine2 ? `, ${m.addressLine2}` : ''}, {m.city} {m.state} {m.postalCode}
                </div>
              </div>
            ))}
          </div>

          {error ? <div className="pb-note pb-note--flag" role="alert">{error}</div> : null}

          <div className="q-actions" style={{ flexDirection: 'column', marginTop: 'var(--pb-3)' }}>
            <button type="button" className="pb-btn pb-btn--lg" onClick={() => navigate('/dashboard')}>
              That&rsquo;s the same property &mdash; take me there
            </button>
            <button
              type="button"
              className="pb-btn pb-btn--ghost pb-btn--lg"
              onClick={() => void create(true)}
              disabled={busy}
            >
              {busy ? 'Saving\u2026' : 'No, this is a different unit \u2014 add it'}
            </button>
            <button type="button" className="pb-btn pb-btn--ghost" onClick={() => setDupMatches(null)}>
              Go back and edit
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flow flow--single">
      <div className="stage-card">
        <h1>Let&rsquo;s get a few details.</h1>
        <p className="stage-card__lede">
          So we know who we&rsquo;re working with and where the job is. We&rsquo;ll use this to follow up
          and to keep your projects together.
        </p>

        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--pb-3)' }}>
          <Field label="Full name" value={form.fullName} onChange={(v) => set('fullName', v)} required />
          <Field label="Phone" value={form.phone} onChange={(v) => set('phone', v)} type="tel" />
          <Field label="Email" value={form.email} onChange={(v) => set('email', v)} type="email" />

          <div className="progress__count" style={{ marginTop: 'var(--pb-2)' }}>WHERE&rsquo;S THE WORK?</div>
          <Field label="Street address" value={form.addressLine1} onChange={(v) => set('addressLine1', v)} required />
          <Field label="Apt / unit (optional)" value={form.addressLine2} onChange={(v) => set('addressLine2', v)} />
          <Field label="City" value={form.city} onChange={(v) => set('city', v)} required />
          <Field label="State" value={form.state} onChange={(v) => set('state', v)} required />
          <Field label="ZIP" value={form.postalCode} onChange={(v) => set('postalCode', v)} required />

          <label className="pb-label" htmlFor="referralSource">How did you hear about us?</label>
          <select
            id="referralSource"
            className="pb-field"
            value={form.referralSource}
            onChange={(e) => set('referralSource', e.target.value)}
          >
            <option value="">Choose one</option>
            {SOURCES.map((s) => (
              <option key={s.code} value={s.code}>{s.label}</option>
            ))}
          </select>

          {isReferral ? (
            <>
              <label className="pb-label" htmlFor="referrer">Who referred you?</label>
              <select
                id="referrer"
                className="pb-field"
                value={referrers.some((r) => r.name === form.referralDetail) ? form.referralDetail : ''}
                onChange={(e) => set('referralDetail', e.target.value)}
              >
                <option value="">Pick a name, or type below</option>
                {referrers.map((r) => (
                  <option key={r.id} value={r.name}>{r.name}</option>
                ))}
              </select>
              <Field
                label="Or type who referred you"
                value={form.referralDetail}
                onChange={(v) => set('referralDetail', v)}
              />
            </>
          ) : null}

          {error ? <div className="pb-note pb-note--flag" role="alert">{error}</div> : null}

          <button type="submit" className="pb-btn pb-btn--lg" disabled={busy || !canSubmit}>
            {busy ? 'Saving\u2026' : 'Continue'}
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({
  label, value, onChange, type = 'text', required = false,
}: {
  label: string; value: string; onChange: (v: string) => void; type?: string; required?: boolean;
}): JSX.Element {
  const id = label.replace(/\s+/g, '-').toLowerCase();
  return (
    <div className="subfield">
      <label className="pb-label" htmlFor={id}>{label}</label>
      <input
        id={id}
        className="pb-field"
        type={type}
        value={value}
        required={required}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}
